import React, { useCallback } from 'react';
import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isNullOrUndef } from 'chart.js/helpers';
import {enqueueSnackbar} from 'notistack';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {Logger, toString} from 'utilities';
import { SymbolHandler } from '../utilities/utilities';

const PREFIX = 'CellReferenceBar';

const classes = {
  toolbarContainer: `${PREFIX}-toolbarContainer`,
  container: `${PREFIX}-container`,
  padLeft: `${PREFIX}-padLeft`,
  keyContainer: `${PREFIX}-keyContainer`,
  valueContainer: `${PREFIX}-valueContainer`,
  fxLabel: `${PREFIX}-fxLabel`,
  value: `${PREFIX}-value`
};

interface CellReferenceBarProps {
  id: string;
  cell: ICell;
  referencedValues: Record<string, any>;
  matchCurrencyPadding?: boolean;
}
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.toolbarContainer}`]: {
    height: theme.spacing(4),
  },
  [`& .${classes.container}`]: {
    fontFamily: 'Roboto Condensed',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
    height: theme.spacing(4),
    width: '100%',
  },

  [`& .${classes.padLeft}`]: {
    marginLeft: theme.spacing(3),
  },

  [`& .${classes.keyContainer}`]: {
    width: theme.spacing(4.375),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '0.75rem',
  },

  [`& .${classes.valueContainer}`]: {
    paddingLeft: theme.spacing(0.5),
    fontSize: '0.875rem',
    cursor: 'copy',
    border: 'none',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
  },

  [`& .${classes.fxLabel}`]: {
    paddingRight: theme.spacing(0.5),
    fontWeight: 600,
  },

  [`& .${classes.value}`]: {
    textTransform: 'uppercase',
  }
}));

function handleCellExpr(cell: ICell<object>) {
  if (typeof cell.expr === 'object') {
    return JSON.stringify(cell.expr);
  }
  if (cell.sheet && cell.isExpr) {
    const symbolHandler = new SymbolHandler(cell);
    return symbolHandler.getExpressionList();
  }
  return cell.expr;
}

function handleReferencedValue(cell: ICell<object>, referencedValues: Record<string, any>) {
  if (Array.isArray(cell.value)) {
    const values: string[] = [];
    cell.value.forEach((value: any) => {
      values.push(referencedValues[cell.alias][value]);
    });
    return values.join(', ');
  }
  return referencedValues[cell.alias][cell.value as string];
}

const CellReferenceBar: React.FC<CellReferenceBarProps> = ({ id, cell, referencedValues, matchCurrencyPadding }) => {
  const getValue = useCallback((): string | React.ReactNode => {
    if (!isNullOrUndef(cell.value) && typeof cell.value !== 'object') {
      if (cell.hidden) {
        return '';
      }
      if (cell?.alias && referencedValues?.[cell.alias]) {
        return handleReferencedValue(cell, referencedValues);
      }
      if (cell.expr) {
        return handleCellExpr(cell);
      }
      return toString(cell.value);
    }
    return '';
  }, [cell, referencedValues]);

  const wrapValue = useCallback((value: any): React.ReactNode => {
    if (Array.isArray(value)) {
      // index is the best thing we have in terms of a unique key
      // eslint-disable-next-line react/no-array-index-key
      return value.map((item, index) => (<span key={index}>{item}</span>))
    }
    return value;
  }, []);

  const getClipboardValue = useCallback((useValue = false): string => {
    const value = getValue();

    const isValueNullish = value === undefined || value === null;
    if (isValueNullish) {
      return '';
    }
    //   if the value is not a list return the value
    if (!Array.isArray(value)) {
      return value as string;
    }
    return (value).map(item => {
      const isColoredSymbolComponent = item?.props;
      if (isColoredSymbolComponent) {
        if (useValue) {
          return item.props.value;
        }
        return item.props.symbol;
      }
      return item;
    }).join('');
  }, [getValue]);

  const handleClick = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.ctrlKey) {
      await navigator.clipboard.writeText(getClipboardValue(true));
      return;
    }
    try {
      await navigator.clipboard.writeText(getClipboardValue());
    } catch (error) {
      const msg = 'Error copying to clipboard';
      Logger.error(msg, error);
      enqueueSnackbar(msg, {variant: 'error'});
    }
  }, [getClipboardValue]);

  return (
    <Root id={`${id}__toolbar`} className={classes.toolbarContainer}>
      <Box
        display="flex"
        className={matchCurrencyPadding ? `${classes.container} ${classes.padLeft}` : classes.container}>
        <Box display="flex" alignItems="center" justifyContent="center" className={classes.keyContainer}>
          <span>{cell.key ?? '-'}</span>
        </Box>
        <Box display="flex" alignItems="center" sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Tooltip title="Click to copy formula to clipboard" placement="bottom">
            <button type="button" className={classes.valueContainer} onClick={handleClick}>
              <span className={classes.fxLabel}>(fx)</span>
              <span className={classes.value}>{wrapValue(getValue())}</span>
            </button>
          </Tooltip>
        </Box>
      </Box>
    </Root>
  );
};


export default CellReferenceBar;
